import { isSalesViewEnabled } from '@ab-core-functions/sales-view';
import {
    userCanSeeBestPrice as userCanSeeBestPriceFunction,
    userHasOnlySalesView as userHasOnlySalesViewFunction
} from '@ab-core-functions/user-permission';
import type { ProductDocument } from '@ab-core/graphql/dist';
import type { PositionPriceObject } from '@ab-core/graphql/dist/index';
import Headline from '@ab-core/headline';
import { useAuth } from '@ab-core/hooks';
import { useBestPrice } from '@ab-core/hooks-v2';
import { Loader } from '@ab-core/loader';
import useText from '@ab-core/localization/src/useText';
import type { ModalType } from '@ab-core/modal';
import Modal from '@ab-core/modal';
import Spacer from '@ab-core/spacer';
import type { FC } from 'react';
import React from 'react';
import { BestPriceList } from './bestPrice';
import { Border, BoxDivider, Content } from './styled';
import { SurchargesAndScalePrices } from './surchargesScaleprices';

type PriceCalculationProps = Pick<ProductDocument, 'sku' | 'quantityUnit'> &
    Pick<ModalType, 'open' | 'onClose'> & {
        quantityUnit?: string;
        positionPriceObject?: PositionPriceObject;
        quantity?: number;
    };

export const PriceCalculation: FC<PriceCalculationProps> = (props) => {
    const { open = false, onClose, quantityUnit, positionPriceObject, quantity = 1, sku = '' } = props;
    const { user } = useAuth();
    const isSalesView = isSalesViewEnabled(user?.activeAccount?.accountNumber);

    const userHasOnlySalesView = userHasOnlySalesViewFunction();
    const userCanSeeBestPrice = userCanSeeBestPriceFunction();
    const hasBestPrice = userCanSeeBestPrice && !isSalesView && !userHasOnlySalesView;

    const { bestPrice, loading } = useBestPrice({
        quantity,
        productId: sku,
        priceReference: positionPriceObject?.priceObject?.priceReference || 1,
        skip: !hasBestPrice || !open || sku === ''
    });

    return (
        <Modal open={open} onClose={onClose} headerText={useText('priceCalculation.title')} md={8} lg={7} xl={5}>
            <Spacer size="xs" />
            {loading ? (
                <Border>
                    <Loader />

                    <Headline>Berechne Preise....</Headline>
                </Border>
            ) : (
                <Content>
                    {positionPriceObject && (
                        <Border>
                            <SurchargesAndScalePrices {...positionPriceObject} quantityUnit={quantityUnit || 'Stück'} />
                        </Border>
                    )}
                    {hasBestPrice && (
                        <>
                            <BoxDivider />
                            <Border>
                                <BestPriceList
                                    {...bestPrice}
                                    sku={bestPrice?.sku ?? ''}
                                    priceReference={positionPriceObject?.priceObject?.priceReference}
                                    quantityUnit={quantityUnit || 'Stück'}
                                />
                            </Border>
                        </>
                    )}
                </Content>
            )}
        </Modal>
    );
};
